/* eslint react/no-multi-comp: 0, react/prop-types: 0 */
import React from 'react';
import { Collapse as AntdCollapse, theme } from 'antd';

import { tokens } from '../theme/tokens';
import './Accordion.scss';

// Owned Accordion family — MVC-native shells replacing the `@relo/sports-components`
// gds-accordion parts. The composition stays the familiar caller-owned shape
// (Container > List > Item(isActive) > { Title(onClick), Icon, ChildItems }); open
// state is owned by the caller as before. Styling is an owned `accordion*` class
// contract (see Accordion.scss) driven by the `Collapse` design token + the app's
// runtime brand palette rather than the legacy GDS stylesheet. `AntdCollapse` is
// re-exported as the escape hatch for callers who want AntD's data-driven Collapse.
//
// `src/ui` is extraction-clean (no `@app/*` imports), so the class joiner is inlined.
type ClassArg = string | false | null | undefined | Record<string, unknown>;

const cx = (...args: ClassArg[]): string => {
    const classes: string[] = [];

    for (const arg of args) {
        if (!arg) continue;

        if (typeof arg === 'string') {
            classes.push(arg);
        } else if (typeof arg === 'object') {
            for (const [key, value] of Object.entries(arg)) {
                if (value) classes.push(key);
            }
        }
    }

    return classes.join(' ');
};

export type AccordionContext =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'quaternary';

export type AccordionSize = 'sm';

export type AccordionContainerProps = React.HTMLAttributes<HTMLDivElement> & {
    /** Brand accent context threaded to the surface. */
    context?: AccordionContext;
    /** Legacy white surface modifier. */
    isWhite?: boolean;
    /** Legacy dark surface modifier. */
    isDark?: boolean;
};

export type AccordionListProps = React.HTMLAttributes<HTMLUListElement>;

export type AccordionItemProps = React.LiHTMLAttributes<HTMLLIElement> & {
    context?: AccordionContext;
    isDark?: boolean;
    /** Caller-owned open flag; drives the active styling. */
    isActive?: boolean;
};

export type AccordionTitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
    context?: AccordionContext;
    isDark?: boolean;
    size?: AccordionSize;
};

export type AccordionIconProps = React.HTMLAttributes<HTMLElement> & {
    isWhite?: boolean;
    size?: AccordionSize;
};

export type AccordionChildItemsProps =
    React.HTMLAttributes<HTMLUListElement> & {
        isDark?: boolean;
        size?: AccordionSize;
    };

const AccordionContainer = React.forwardRef<
    HTMLDivElement,
    AccordionContainerProps
>(({ context, isWhite, isDark, className, children, style, ...rest }, ref) => {
    // Styling is token-driven: the `Collapse`/global theme values from tokens.ts
    // (resolved through UiProvider) are read here and pushed down as CSS custom
    // properties that Accordion.scss consumes — no hardcoded colours in the scss.
    const { token } = theme.useToken();
    // The brand `Collapse` token override from tokens.ts (headerBg / colorBorder)
    // drives the accordion surface + separators; the rest comes from the global
    // theme. Both resolve through UiProvider — no hardcoded values in the scss.
    const collapse = (tokens.components?.Collapse ?? {}) as {
        headerBg?: string;
        colorBorder?: string;
    };
    const themeVars = {
        '--accordion-surface': collapse.headerBg ?? token.colorFillTertiary,
        '--accordion-active-bg': token.colorBgContainer,
        '--accordion-border': collapse.colorBorder ?? token.colorBorder,
        '--accordion-text': token.colorText,
        '--accordion-on-dark': token.colorTextLightSolid,
        '--accordion-radius': `${token.borderRadiusSM}px`,
        '--accordion-title-size': `${token.fontSizeLG}px`,
        '--accordion-title-size-sm': `${token.fontSize}px`,
    } as React.CSSProperties;

    return (
        <div
            ref={ref}
            className={cx(
                'accordion',
                {
                    [`accordion--${context}`]: context,
                    'accordion--white': isWhite && !context,
                    'accordion--dark': isDark && !context,
                    [`accordion--white-${context}`]: isWhite && context,
                    [`accordion--dark-${context}`]: isDark && context,
                },
                className,
            )}
            style={{ ...themeVars, ...style }}
            {...rest}
        >
            {children}
        </div>
    );
});
AccordionContainer.displayName = 'AccordionContainer';

const AccordionList = React.forwardRef<HTMLUListElement, AccordionListProps>(
    ({ className, children, ...rest }, ref) => (
        <ul ref={ref} className={cx('accordion__list', className)} {...rest}>
            {children}
        </ul>
    ),
);
AccordionList.displayName = 'AccordionList';

const AccordionItem = React.forwardRef<HTMLLIElement, AccordionItemProps>(
    ({ context, isDark, isActive, className, children, ...rest }, ref) => (
        <li
            ref={ref}
            className={cx(
                'accordion__item',
                {
                    'accordion__item--active': isActive,
                    'accordion__item--dark': isDark,
                    [`accordion__item--${context}`]: context,
                },
                className,
            )}
            {...rest}
        >
            {children}
        </li>
    ),
);
AccordionItem.displayName = 'AccordionItem';

const AccordionTitle = React.forwardRef<
    HTMLHeadingElement,
    AccordionTitleProps
>(
    (
        {
            context,
            isDark,
            size,
            className,
            children,
            onClick,
            onKeyDown,
            ...rest
        },
        ref,
    ) => {
        // a11y: when the title is the clickable toggle, make it keyboard-operable
        // as a button (Enter/Space) in addition to the caller's click handler.
        const handleKeyDown = (
            event: React.KeyboardEvent<HTMLHeadingElement>,
        ) => {
            onKeyDown?.(event);
            if (onClick && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onClick(
                    event as unknown as React.MouseEvent<HTMLHeadingElement>,
                );
            }
        };

        return (
            <h4
                ref={ref}
                className={cx(
                    'accordion__item-title',
                    {
                        'accordion__item-title--dark': isDark,
                        [`accordion__item-title--${size}`]: size,
                        [`accordion__item-title--${context}`]: context,
                    },
                    className,
                )}
                onClick={onClick}
                onKeyDown={onClick ? handleKeyDown : onKeyDown}
                role={onClick ? 'button' : undefined}
                tabIndex={onClick ? 0 : undefined}
                {...rest}
            >
                {children}
            </h4>
        );
    },
);
AccordionTitle.displayName = 'AccordionTitle';

const AccordionIcon = React.forwardRef<HTMLElement, AccordionIconProps>(
    ({ isWhite, size, className, ...rest }, ref) => (
        <i
            ref={ref}
            className={cx(
                'accordion__item-icon',
                {
                    'accordion__item-icon--white': isWhite,
                    [`accordion__item-icon--${size}`]: size,
                },
                className,
            )}
            aria-hidden="true"
            {...rest}
        />
    ),
);
AccordionIcon.displayName = 'AccordionIcon';

const AccordionChildItems = React.forwardRef<
    HTMLUListElement,
    AccordionChildItemsProps
>(({ isDark, size, className, children, ...rest }, ref) => (
    <ul
        ref={ref}
        className={cx(
            'accordion__child-items',
            {
                'accordion__child-items--dark': isDark,
                [`accordion__child-items--${size}`]: size,
            },
            className,
        )}
        {...rest}
    >
        {children}
    </ul>
));
AccordionChildItems.displayName = 'AccordionChildItems';

export {
    AccordionContainer,
    AccordionList,
    AccordionItem,
    AccordionTitle,
    AccordionIcon,
    AccordionChildItems,
    AntdCollapse,
};
